/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5 · existing AKAS availability drawer */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 4,
  });

const normalizeMaterials = (variant = {}) => {
  if (Array.isArray(variant?.availability_materials) && variant.availability_materials.length) {
    return variant.availability_materials;
  }

  return (variant?.materials || []).map((material) => {
    const isBaseUnit = ["gram", "gr", "g", "ml", "milliliter", "pcs", "piece"].includes(
      String(material?.unit || "").toLowerCase(),
    );
    const required = isBaseUnit ? Number(material?.weight || material?.qty || 0) : Number(material?.qty || 0);
    const available = Number(material?.stock || 0);

    return {
      material_id: material?.material_id,
      material_name: material?.material_name || "Material",
      required_qty: required,
      available_stock: available,
      shortage_qty: Math.max(0, required - available),
      unit: material?.material_unit || material?.unit || "unit",
      is_sufficient: required > 0 && available >= required,
    };
  });
};

const VariantAvailabilityDrawer = ({ variant, onClose }) => {
  useEffect(() => {
    if (!variant || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [variant, onClose]);

  if (!variant) return null;

  const materials = normalizeMaterials(variant);
  const missingRecipe = variant?.inventory_mode === "made_to_order" && materials.length === 0;
  const displayName =
    variant?.product_group_name && variant?.variant_label
      ? `${variant.product_group_name} · ${variant.variant_label}`
      : variant?.name || variant?.variant_label || "Produk";

  const content = (
    <div className="pos-availability-layer" role="presentation" onMouseDown={onClose}>
      <aside
        className="pos-availability-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="variant-availability-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="pos-availability-header">
          <div>
            <span>INFORMASI KETERSEDIAAN</span>
            <h3 id="variant-availability-title">{displayName}</h3>
            <p>Periksa bahan resep berikut untuk mengetahui mengapa varian belum dapat dijual.</p>
          </div>
          <button type="button" className="btn-close" aria-label="Tutup informasi" onClick={onClose} />
        </header>

        <div className="pos-availability-body">
          {missingRecipe ? (
            <div className="pos-availability-empty">
              <span>!</span>
              <strong>Resep belum diatur</strong>
              <p>Tambahkan bahan baku dan takaran resep varian ini melalui halaman Produk.</p>
            </div>
          ) : (
            <div className="pos-availability-list">
              {materials.map((material, index) => {
                const sufficient = material?.is_sufficient !== false && Number(material?.shortage_qty || 0) <= 0;

                return (
                  <div
                    className={`pos-availability-item ${sufficient ? "is-ready" : "is-short"}`}
                    key={`${material?.material_id || material?.material_name}-${index}`}>
                    <div className="pos-availability-item-top">
                      <div>
                        <strong>{material?.material_name || "Material"}</strong>
                        <small>{sufficient ? "Stok mencukupi" : "Stok tidak mencukupi"}</small>
                      </div>
                      <span>{sufficient ? "Siap" : "Kurang"}</span>
                    </div>
                    <div className="pos-availability-numbers">
                      <div>
                        <small>Dibutuhkan</small>
                        <strong>
                          {formatQuantity(material?.required_qty)} {material?.unit || ""}
                        </strong>
                      </div>
                      <div>
                        <small>Stok saat ini</small>
                        <strong>
                          {formatQuantity(material?.available_stock)} {material?.unit || ""}
                        </strong>
                      </div>
                      <div>
                        <small>Kekurangan</small>
                        <strong>
                          {formatQuantity(material?.shortage_qty)} {material?.unit || ""}
                        </strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className="pos-availability-footer">
          <div>
            <strong>Solusi cepat</strong>
            <span>Lakukan pembelian atau koreksi stok material terlebih dahulu.</span>
          </div>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Saya mengerti
          </button>
        </footer>
      </aside>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
};

export default VariantAvailabilityDrawer;
